/**
 * onTimeDispatchLocationSummaryWorkbookBuilder.ts
 * Builds a .xlsx workbook for the On-Time Dispatch Location Summary export.
 *
 * Layout:
 *   row 1  → column headers (bold, light-blue fill for metric columns)
 *   row 2  → Grand Total row (blue fill + dark-blue bold font)
 *   row 3+ → sorted data rows
 *
 * Percent columns are stored as plain text strings (e.g. "98.00%") using the
 * same toFixed(2) rounding as the UI — guaranteeing Excel displays exactly
 * what is shown on screen.
 */

import ExcelJS from 'exceljs';

export interface LocationSummaryExportRow {
  dimensions: Record<string, string>;
  planned: number;
  completed: number;
  completedPct: number;
  incomplete: number;
  onTime: number;
  onTimePct: number;
  daeN: number;
  daePct: number;
}

export interface BuildOnTimeLocationSummaryWorkbookParams {
  /** Ordered list of dimension keys that are currently visible (e.g. ['region', 'market']). */
  dimensionKeys: string[];
  /** Human-readable labels matching dimensionKeys (same order). */
  dimensionLabels: string[];
  /** Grand-total row (dimensions will be overridden with "Grand Total" / ""). */
  totalRow: LocationSummaryExportRow;
  /** Sorted data rows. */
  rows: LocationSummaryExportRow[];
}

const HEADER_FILL_HEX = 'F8FAFD';
const TOTAL_FILL_HEX  = 'F3F7FD';
const TOTAL_FONT_HEX  = '003A7D';

const GRAIN_COL_WIDTH  = 20;
const METRIC_COL_WIDTH = 18;

/**
 * Format a percent value exactly as the UI does: toFixed(2) + "%" suffix.
 * Stored as a plain text string so Excel renders identically to the UI
 * with no floating-point drift from numeric percent formats.
 * e.g. 98.0 → "98.00%", 73.09... → "73.09%", 97.7245 → "97.72%"
 */
const fmtPct = (v: number): string => `${v.toFixed(2)}%`;

const METRIC_HEADERS = [
  'Planned Loads',
  'Completed Loads',
  'Completed %',
  'Incomplete Loads',
  'On-Time Loads',
  'On-Time %',
  'Driver Arrived as Expected',
  'Arrived as Expected %',
];

const INT_FORMAT = '#,##0';

const rowValues = (
  row: LocationSummaryExportRow,
  dimensionKeys: string[],
): Array<string | number> => [
  ...dimensionKeys.map((k) => row.dimensions[k] ?? ''),
  row.planned,
  row.completed,
  fmtPct(row.completedPct),
  row.incomplete,
  row.onTime,
  fmtPct(row.onTimePct),
  row.daeN,
  fmtPct(row.daePct),
];

export const buildOnTimeLocationSummaryWorkbook = ({
  dimensionKeys,
  dimensionLabels,
  totalRow,
  rows,
}: BuildOnTimeLocationSummaryWorkbookParams): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Location Summary', {
    views: [{ state: 'frozen', xSplit: dimensionKeys.length, ySplit: 2 }],
  });

  const totalCols = dimensionKeys.length + METRIC_HEADERS.length;

  // Column widths
  ws.columns = [
    ...dimensionKeys.map(() => ({ width: GRAIN_COL_WIDTH })),
    ...METRIC_HEADERS.map(() => ({ width: METRIC_COL_WIDTH })),
  ];

  // Row 1 — headers
  const headerValues: string[] = [...dimensionLabels, ...METRIC_HEADERS];
  const headerRow = ws.addRow(headerValues);
  headerRow.eachCell((cell, colIdx) => {
    cell.font = { bold: true, size: 13 };
    if (colIdx > dimensionKeys.length) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_FILL_HEX}` } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    }
  });

  // Row 2 — Grand Total
  const totalDimensions: Record<string, string> = Object.fromEntries(
    dimensionKeys.map((k, i) => [k, i === 0 ? 'Grand Total' : '']),
  );
  const totalXlsxRow = ws.addRow(rowValues({ ...totalRow, dimensions: totalDimensions }, dimensionKeys));
  totalXlsxRow.eachCell((cell, colIdx) => {
    cell.font = { bold: true, size: 13, color: { argb: `FF${TOTAL_FONT_HEX}` } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${TOTAL_FILL_HEX}` } };
    if (colIdx > dimensionKeys.length) {
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    }
  });

  // Rows 3+ — data
  rows.forEach((row) => {
    const xlsxRow = ws.addRow(rowValues(row, dimensionKeys));
    xlsxRow.eachCell((cell, colIdx) => {
      cell.font = { size: 12 };
      if (colIdx > dimensionKeys.length) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });
  });

  // Apply integer format to count columns (rows 2 onwards)
  const intOffsets = [1, 2, 4, 5, 7]; // Planned, Completed, Incomplete, On-Time, DAE N
  const firstDataRowIdx = 2; // grand total
  const lastRowIdx = ws.rowCount;
  for (let rIdx = firstDataRowIdx; rIdx <= lastRowIdx; rIdx++) {
    for (const intOffset of intOffsets) {
      const colIdx = dimensionKeys.length + intOffset;
      ws.getCell(rIdx, colIdx).numFmt = INT_FORMAT;
    }
  }

  return workbook;
};
